import React from "react";
import {
  Typography,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Autocomplete,
  Popper,
  Box,
  Divider,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import { fieldSx, US_STATES, FIELD_MAX_LENGTHS } from "./constants";

const STATE_OPTIONS = US_STATES.map((s) => `${s.code} — ${s.name}`);

function BelowPopper(props) {
  return <Popper {...props} placement="bottom-start" />;
}

function SectionLabel({ icon: Icon, label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
      <Icon sx={{ fontSize: 15, color: "#09b2f0" }} />
      <Typography
        sx={{
          fontSize: "0.7rem",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "#94A3B8",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export default function PersonalInfoEditForm({
  formData,
  fieldErrors,
  onChange,
  onPhoneBlur,
  onDateChange,
  onStateChange,
  onCancel,
  onSave,
  saving,
  isDirty,
}) {
  const stateDisplayValue = (() => {
    const match = US_STATES.find(
      (s) => s.code === formData.state || s.name === formData.state,
    );
    return match ? `${match.code} — ${match.name}` : null;
  })();

  return (
    <Box sx={{ px: 3, py: 3 }}>
      <Stack spacing={3}>
        {/* Personal Details */}
        <Box>
          <SectionLabel icon={PersonIcon} label="Personal Details" />
          <Divider sx={{ mb: 2, borderColor: "#F1F5F9" }} />
          <Stack spacing={2}>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField
                required
                label="First Name"
                value={formData.firstName}
                onChange={onChange("firstName")}
                error={!!fieldErrors.firstName}
                helperText={fieldErrors.firstName}
                fullWidth
                slotProps={{ htmlInput: { maxLength: FIELD_MAX_LENGTHS.firstName } }}
                sx={fieldSx}
              />
              <TextField
                required
                label="Last Name"
                value={formData.lastName}
                onChange={onChange("lastName")}
                error={!!fieldErrors.lastName}
                helperText={fieldErrors.lastName}
                fullWidth
                slotProps={{ htmlInput: { maxLength: FIELD_MAX_LENGTHS.lastName } }}
                sx={fieldSx}
              />
            </Box>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date of Birth"
                value={formData.birthday ? dayjs(formData.birthday) : null}
                onChange={onDateChange}
                maxDate={dayjs().subtract(18, "year")}
                minDate={dayjs("1900-01-01")}
                slotProps={{
                  textField: {
                    required: true,
                    fullWidth: true,
                    error: !!fieldErrors.birthday,
                    helperText: fieldErrors.birthday || "Must be 18 or older",
                    sx: fieldSx,
                  },
                }}
              />
            </LocalizationProvider>
          </Stack>
        </Box>

        {/* Contact */}
        <Box>
          <SectionLabel icon={ContactPhoneIcon} label="Contact" />
          <Divider sx={{ mb: 2, borderColor: "#F1F5F9" }} />
          <TextField
            required
            label="Phone Number"
            value={formData.phoneNumber}
            onChange={onChange("phoneNumber")}
            onBlur={onPhoneBlur}
            error={!!fieldErrors.phoneNumber}
            helperText={fieldErrors.phoneNumber || "Format: (###) ###-####"}
            fullWidth
            slotProps={{ htmlInput: { maxLength: FIELD_MAX_LENGTHS.phoneNumber } }}
            sx={fieldSx}
          />
        </Box>

        {/* Address */}
        <Box>
          <SectionLabel icon={HomeIcon} label="Address" />
          <Divider sx={{ mb: 2, borderColor: "#F1F5F9" }} />
          <Stack spacing={2}>
            <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 2 }}>
              <TextField
                required
                label="Street Address"
                value={formData.streetAddress}
                onChange={onChange("streetAddress")}
                error={!!fieldErrors.streetAddress}
                helperText={fieldErrors.streetAddress}
                fullWidth
                slotProps={{ htmlInput: { maxLength: FIELD_MAX_LENGTHS.streetAddress } }}
                sx={fieldSx}
              />
              <TextField
                label="Unit / Apt #"
                value={formData.unitNumber}
                onChange={onChange("unitNumber")}
                error={!!fieldErrors.unitNumber}
                helperText={fieldErrors.unitNumber}
                fullWidth
                slotProps={{ htmlInput: { maxLength: FIELD_MAX_LENGTHS.unitNumber } }}
                sx={fieldSx}
              />
            </Box>

            <TextField
              required
              label="City"
              value={formData.city}
              onChange={onChange("city")}
              error={!!fieldErrors.city}
              helperText={fieldErrors.city}
              fullWidth
              slotProps={{ htmlInput: { maxLength: FIELD_MAX_LENGTHS.city } }}
              sx={fieldSx}
            />

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <Autocomplete
                options={STATE_OPTIONS}
                value={stateDisplayValue}
                onChange={onStateChange}
                autoHighlight
                autoSelect
                slots={{ popper: BelowPopper }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="State"
                    error={!!fieldErrors.state}
                    helperText={fieldErrors.state}
                    sx={fieldSx}
                  />
                )}
              />

              <TextField
                required
                label="ZIP Code"
                value={formData.zipCode}
                onChange={onChange("zipCode")}
                error={!!fieldErrors.zipCode}
                helperText={fieldErrors.zipCode}
                fullWidth
                slotProps={{ htmlInput: { maxLength: FIELD_MAX_LENGTHS.zipCode } }}
                sx={fieldSx}
              />
            </Box>
          </Stack>
        </Box>
      </Stack>

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
          mt: 3,
          pt: 2.5,
          borderTop: "1px solid #F1F5F9",
        }}
      >
        <Button
          onClick={onCancel}
          startIcon={<CloseIcon sx={{ fontSize: "15px !important" }} />}
          sx={{
            color: "#64748B",
            fontWeight: 600,
            fontSize: "0.78rem",
            textTransform: "none",
            borderRadius: "8px",
            px: 2,
            border: "1px solid #E2E8F0",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#F8FAFC",
              borderColor: "#CBD5E1",
              boxShadow: "none",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onSave}
          disabled={!isDirty || saving}
          startIcon={
            saving ? (
              <CircularProgress size={14} sx={{ color: "#fff" }} />
            ) : (
              <SaveIcon sx={{ fontSize: "15px !important" }} />
            )
          }
          sx={{
            backgroundColor: saving || !isDirty ? "#E2E8F0" : "#f59e0b",
            color: saving || !isDirty ? "#94A3B8" : "#000",
            fontWeight: 700,
            fontSize: "0.78rem",
            textTransform: "none",
            borderRadius: "8px",
            px: 2,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: saving || !isDirty ? "#E2E8F0" : "#f59e0b",
              opacity: saving || !isDirty ? 1 : 0.85,
              boxShadow: "none",
            },
          }}
        >
          {saving ? "Saving…" : "Save Changes"}
        </Button>
      </Box>
    </Box>
  );
}
